import { css, SerializedStyles } from '@emotion/core'
import range from 'lodash.range'
import React from 'react'

import { breakpoints } from '../constants/breakPoints'
import { HEADER_HEIGHT } from '../constants/header'

interface Props {
  id?: string
  columns?: number
  columnsTemplate?: string
  twoColumnsForTablet?: boolean
  twoColumnsForMobile?: boolean
  noBreakForTablet?: boolean
  noBreak?: boolean
  columnGap?: number
  rowGap?: number
  rowGapMobile?: number
  animated?: boolean
  breakPoint?: number
  noBorderRadius?: boolean
  noPadding?: boolean
  narrow?: boolean
  maxWidth?: string
  bigFirstRow?: boolean
  highlighted?: 'row' | 'firstColumn' | 'lastColumn'
  responsiveReversed?: boolean
  className?: string
  alignContent?: string
  css?: SerializedStyles | (boolean | SerializedStyles)[]
  onClick?: (event: React.MouseEvent<HTMLElement>) => void
  children?: React.ReactNode
}

const Block: React.FunctionComponent<Props> = ({
  id,
  columns = 1,
  columnsTemplate,
  twoColumnsForTablet = false,
  twoColumnsForMobile = false,
  noBreakForTablet = false,
  noBreak = false,
  columnGap,
  rowGap,
  rowGapMobile,
  animated = false,
  highlighted,
  responsiveReversed = false,
  noBorderRadius = false,
  bigFirstRow = false,
  noPadding = false,
  narrow = false,
  breakPoint = 768,
  maxWidth,
  className,
  alignContent = 'center',
  children,
  css,
  onClick,
}) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const [inViewPort, setInViewPort] = React.useState(true)
  React.useEffect(() => {
    if (ref.current) {
      if (!elementVisible(ref.current)) {
        setInViewPort(false)
      }
    }
  }, [ref.current])

  React.useEffect(() => {
    const intervalId = window.setInterval(() => {
      if (ref.current) {
        if (!inViewPort && elementVisible(ref.current)) {
          setInViewPort(true)
        }
      }
    }, 100)
    return () => window.clearInterval(intervalId)
  }, [ref.current, inViewPort])

  return (
    <div ref={ref} css={[blockStyle, css]} onClick={onClick}>
      {!!id && <div id={id} css={anchorStyle} />}
      {!!highlighted && (
        <div
          css={[
            highlighted === 'row' && blockRowHighlightedStyle,
            (highlighted === 'firstColumn' || highlighted === 'lastColumn') && blockColumHighlighedStyle,
            highlighted === 'firstColumn' && blockFirstColumHighlighedStyle,
            highlighted === 'lastColumn' && blockLastColumHighlighedStyle,
            noBorderRadius && noBorderRadiusStyle,
          ]}
          className={className}
        />
      )}
      <div
        css={[
          innerStyle(
            columns,
            alignContent,
            columnsTemplate,
            twoColumnsForTablet,
            twoColumnsForMobile,
            breakPoint,
            noBreakForTablet,
            noBreak,
            columnGap,
            rowGap,
            rowGapMobile,
            maxWidth
          ),
          !animated || inViewPort ? innerStyleInViewport : innerStyleNotInViewport,
          responsiveReversed && innerResponsiveReversedStyle(columns, breakPoint),
          bigFirstRow && bigFirstRowStyle(columns),
          noPadding && noPaddingStyle,
          narrow && narrowStyle,
        ]}
        className={className}
      >
        {children}
      </div>
    </div>
  )
}

export default Block

export const breakPoint = 768

const blockStyle = css`
  position: relative;
`

const blockRowHighlightedStyle = css`
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background-color: #f4f5f7;
  border-radius: 0px 200px 0px 0px;
  z-index: -1;

  @media screen and (max-width: ${breakPoint}px) {
    border-radius: 0px 100px 0px 0px;
  }
`

const blockColumHighlighedStyle = css`
  position: absolute;
  left: 0;
  top: 30px;
  right: 0;
  bottom: 30px;
  background-color: #f4f6f7;
  z-index: -1;
`

const blockFirstColumHighlighedStyle = css`
  right: 30%;
  border-radius: 0px 0px 200px 0px;

  @media screen and (max-width: ${breakPoint}px) {
    right: 0;
    border-radius: 0px 100px 0px 0px;
  }
`

const blockLastColumHighlighedStyle = css`
  left: 60%;
  border-radius: 0px 0px 0px 200px;

  @media screen and (max-width: ${breakPoint}px) {
    left: 0;
    border-radius: 0px 100px 0px 0px;
  }
`

const anchorStyle = css`
  position: absolute;
  left: 0;
  top: -${Math.max(HEADER_HEIGHT - 1, 0)}px;
`

const innerStyle = (
  columns: number,
  alignContent?: string,
  columnsTemplate?: string,
  twoColumnsForTablet?: boolean,
  twoColumnsForMobile?: boolean,
  breakPoint?: number,
  noBreakForTablet?: boolean,
  noBreak?: boolean,
  columnGap?: number,
  rowGap?: number,
  rowGapMobile?: number,
  maxWidth?: string
) => css`
  max-width: ${maxWidth ? maxWidth : '1136px'};
  margin: 0 auto;
  display: grid;
  align-items: ${alignContent ? alignContent : 'center'};
  grid-template-columns: ${columnsTemplate ? columnsTemplate : `repeat(${columns},1fr)`} ${noBreak && '!important'};
  gap: ${rowGap !== undefined ? `${rowGap}rem` : '3rem'} ${columnGap !== undefined ? `${columnGap}rem` : '1rem'};

  & > img {
    width: 100%;
  }

  @media screen and (max-width: ${breakPoint || breakpoints.medium}px) {
    ${twoColumnsForTablet ? 'grid-template-columns: 1fr 1fr' : !noBreakForTablet && 'grid-template-columns: 1fr'};
    row-gap: ${rowGapMobile ? `${rowGapMobile}rem !important` : rowGap ? `${rowGap}rem` : '1.5rem'};
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    grid-template-columns: ${twoColumnsForMobile && !noBreak ? '1fr 1fr' : '1fr'};
    ${!noBreak && 'grid-gap: 1rem;'};
    & > div,
    h2,
    img {
      grid-column: initial;
    }
  }
`

const innerResponsiveReversedStyle = (columns: number, breakPoint: number) => css`
  @media screen and (max-width: ${breakPoint}px) {
    ${range(0, columns)
      .map((i) => `& > *:nth-of-type(${i + 1}) { order: ${columns - i}; }`)
      .join('\n')}
  }
`

const innerStyleNotInViewport = css`
  transition:
    opacity 1.5s ease,
    transform 1.5s ease;
  transform: scale(0.75);
  opacity: 0;
`

const innerStyleInViewport = css`
  transition:
    opacity 1.5s ease,
    transform 1.5s ease;
  opacity: 1;
  transform: scale(1);
`

const noBorderRadiusStyle = css`
  border-radius: 0px;
  @media screen and (max-width: ${breakPoint}px) {
    border-radius: 0px;
  }
`

const bigFirstRowStyle = (columns: number) => css`
  grid-template-columns: 60% repeat(${columns - 1}, 1fr);
`

const noPaddingStyle = css`
  padding-top: 0px;
  padding-bottom: 0px;
  @media screen and (max-width: ${breakPoint}px) {
    padding-top: 0px;
    padding-bottom: 0px;
  }
`

const narrowStyle = css`
  max-width: 800px;
`

function elementVisible(element: HTMLDivElement) {
  const rect = element.getBoundingClientRect()
  const top = rect.top
  const bot = rect.bottom
  // Only completely visible elements return true:
  // return top >= 0 && bot <= window.innerHeight;
  // Partially visible elements return true:
  return top < window.innerHeight && bot >= 0
}
